import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PaymentMethodsManager from "@/components/PaymentMethodsManager";
import AffidamentiFunnelDialog from "@/components/AffidamentiFunnelDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Wallet, RefreshCw } from "lucide-react";
import FunnelTriggerButton from "@/components/FunnelTriggerButton";
import { toast } from "sonner";

const eurN = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("it-IT")}`);

function AffRow({ r, onSaved, canEdit }) {
  const base = r.affidamento_max != null ? r.affidamento_max : (r.fido_massimo != null ? r.fido_massimo : "");
  const [val, setVal] = useState(base);
  const [saving, setSaving] = useState(false);
  const dirty = String(val) !== String(base);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/partners/${r.id}/affidamento`, { affidamento_max: val === "" ? null : val }); toast.success(`Fido aggiornato: ${r.name}`); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  const [showBlock, setShowBlock] = useState(false);
  const [ins, setIns] = useState("");
  const [blocking, setBlocking] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const verifica = async () => {
    setVerifying(true);
    try { await api.post(`/partners/${r.id}/affidamento-verifica`); toast.success(`Affidamento verificato: ${r.name}`); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setVerifying(false); }
  };
  const block = async () => {
    const imp = Number(ins);
    if (!imp || imp <= 0) { toast.error("Indica l'importo dell'insoluto"); return; }
    setBlocking(true);
    try { await api.put(`/partners/${r.id}/insoluto`, { attivo: true, importo: imp }); toast.success(`Affidamento bloccato per insoluto: ${r.name}`); setShowBlock(false); setIns(""); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBlocking(false); }
  };
  const unblock = async () => {
    setBlocking(true);
    try { await api.put(`/partners/${r.id}/insoluto`, { attivo: false }); toast.success("Blocco insoluto rimosso"); onSaved && onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBlocking(false); }
  };
  const b = r.bilancio || {};
  const B = ({ label, v, money }) => (
    <><span className="text-muted-foreground">{label}</span><span className="text-right font-medium">{v == null || v === "" ? "—" : (money ? eurN(v) : v)}</span></>
  );
  return (
    <tr className={`border-b border-border/50 align-top ${r.sconfino ? "bg-red-500/10" : ""}`} data-testid={`aff-row-${r.id}`}>
      <td className="py-2 pr-3">
        <div className="flex items-center gap-2">
          <Link to={r.tipo === "prospect" ? `/prospect/${r.id}` : `/partners/${r.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline" data-testid={`aff-link-${r.id}`}>{r.name}<ExternalLinkIcon /></Link>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${r.tipo === "prospect" ? "bg-sky-500/15 text-sky-600 dark:text-sky-400" : "bg-muted text-muted-foreground"}`} data-testid={`aff-tipo-${r.id}`}>{r.tipo === "prospect" ? "Prospect" : "Cliente"}</span>
          {r.sconfino && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`aff-sconfino-${r.id}`}>Oltre fido</span>}
          {r.insoluto?.attivo && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`aff-insoluto-${r.id}`}>Insoluto € {Number(r.insoluto.importo).toLocaleString("it-IT")}</span>}
        </div>
        {r.fascia_rischio && <div className="text-[11px] text-muted-foreground">{r.fascia_rischio}</div>}
      </td>
      <td className="py-2 pr-3">
        <div className="flex flex-col items-start gap-1" data-testid={`aff-analisi-${r.id}`}>
          {r.analisi_creditsafe && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400" data-testid={`aff-analisi-cs-${r.id}`}>Creditsafe</span>}
          {r.bilancio_presente
            ? <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400" data-testid={`aff-analisi-bil-${r.id}`}>Bilancio</span>
            : <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" title="Analisi bilancio mancante" data-testid={`aff-no-bilancio-${r.id}`}>No bilancio</span>}
          {r.bilancio_presente && r.bilancio_rinnovo && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" title="Carica bilancio aggiornato" data-testid={`aff-bilancio-rinnovo-${r.id}`}>Rinnovo bilancio</span>}
          {r.bilancio_presente && !r.bilancio_rinnovo && r.bilancio_vecchio && <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" title="Carica bilancio aggiornato" data-testid={`aff-bilancio-vecchio-${r.id}`}>Bilancio vecchio</span>}
          {!r.analisi_creditsafe && !r.bilancio_presente && <span className="text-[11px] text-muted-foreground">Nessuna analisi</span>}
          {r.verifica?.due && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" title={`Verifica affidamento SCADUTA (base ${r.verifica.base}). Ultima concessione/verifica: ${r.verifica.ref || "n/d"}. Serve nuova verifica del Back Office.`} data-testid={`aff-verifica-due-${r.id}`}>Verifica scaduta</span>}
          {!r.verifica?.due && r.verifica?.scadenza && r.verifica?.giorni != null && r.verifica.giorni <= 30 && <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" title={`Verifica affidamento entro il ${r.verifica.scadenza} (base ${r.verifica.base})`} data-testid={`aff-verifica-soon-${r.id}`}>Verifica {r.verifica.giorni}gg</span>}
        </div>
      </td>
      <td className="py-2 pr-3 text-xs text-muted-foreground">{r.modalita_pagamento || "—"}</td>
      <td className="py-2 pr-3 text-right"><div className="font-semibold">{eurN(r.ordini_6m.eur)}</div><div className="text-[11px] text-muted-foreground">{r.ordini_6m.n} ord.</div></td>
      <td className="py-2 pr-3 text-right"><div className="font-semibold">{eurN(r.preventivi_6m.eur)}</div><div className="text-[11px] text-muted-foreground">{r.preventivi_6m.n} prev.</div></td>
      <td className="py-2 pr-3 text-right"><div className="font-semibold">{eurN(r.esposizione)}</div><div className="text-[11px] text-muted-foreground">{r.commesse_aperte_n} comm.</div></td>
      <td className="py-2 pr-3 text-right"><div className={`font-bold ${r.residuo != null && r.residuo < 0 ? "text-red-600 dark:text-red-400" : ""}`}>{r.residuo == null ? "—" : eurN(r.residuo)}</div></td>
      <td className="py-2 pr-3">
        <div className="flex items-center gap-1">
          {canEdit ? (
            <>
              <input type="number" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && dirty && save()} className="w-28 rounded border border-border bg-background px-2 py-1 text-right text-sm" data-testid={`aff-input-${r.id}`} />
              {dirty && <Button size="sm" className="h-7 px-2" onClick={save} disabled={saving} data-testid={`aff-save-${r.id}`}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "OK"}</Button>}
            </>
          ) : (
            <span className="font-semibold">{eurN(base)}</span>
          )}
        </div>
        {r.fido_massimo != null && <div className="mt-0.5 text-[11px] text-muted-foreground">gest.: {eurN(r.fido_massimo)}</div>}
        {r.fido_suggerito != null && <div className="mt-0.5 text-[11px] text-primary" data-testid={`aff-suggerito-${r.id}`}>suggerito: {eurN(r.fido_suggerito)}</div>}
        {canEdit && (
          <div className="mt-1">
            {r.insoluto?.attivo ? (
              <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={unblock} disabled={blocking} data-testid={`aff-unblock-${r.id}`}>{blocking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Sblocca affidamento"}</Button>
            ) : showBlock ? (
              <div className="flex items-center gap-1">
                <input type="number" autoFocus placeholder="Importo insoluto €" value={ins} onChange={(e) => setIns(e.target.value)} onKeyDown={(e) => e.key === "Enter" && block()} className="w-32 rounded border border-red-400 bg-background px-2 py-1 text-right text-sm" data-testid={`aff-insoluto-input-${r.id}`} />
                <Button size="sm" variant="destructive" className="h-6 px-2 text-[10px]" onClick={block} disabled={blocking} data-testid={`aff-block-confirm-${r.id}`}>{blocking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Blocca"}</Button>
                <button type="button" onClick={() => { setShowBlock(false); setIns(""); }} className="text-[10px] text-muted-foreground hover:underline">Annulla</button>
              </div>
            ) : (
              <button type="button" onClick={() => setShowBlock(true)} className="text-[10px] font-semibold text-red-600 hover:underline" data-testid={`aff-block-open-${r.id}`}>Blocca affidamento (insoluto)</button>
            )}
          </div>
        )}
        {canEdit && r.tipo === "cliente" && r.verifica?.base && r.verifica.base !== "bilancio" && r.affidamento_effettivo != null && (
          <div className="mt-1 flex items-center gap-1" data-testid={`aff-verifica-wrap-${r.id}`}>
            <button type="button" onClick={verifica} disabled={verifying} className={`text-[10px] font-semibold hover:underline ${r.verifica.due ? "text-red-600" : "text-primary"}`} data-testid={`aff-verifica-btn-${r.id}`}>{verifying ? "..." : (r.verifica.due ? "Verifica ora affidamento" : "Rinnova verifica (6 mesi)")}</button>
            {r.verifica.scadenza && <span className="text-[10px] text-muted-foreground">scad. {r.verifica.scadenza}</span>}
          </div>
        )}
      </td>
      <td className="py-2 text-right text-xs">
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          <B label="Anno" v={b.anno} />
          <B label="Fatturato" v={b.fatturato} money />
          <B label="Patrimonio" v={b.patrimonio} money />
          <B label="Magazzino" v={b.magazzino} money />
          <B label="PFN" v={b.pfn} money />
          <B label="EBITDA" v={b.ebitda} money />
          <B label="Dipendenti" v={b.dipendenti} />
        </div>
      </td>
    </tr>
  );
}

const ExternalLinkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline"><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
);

const SEM = { ok: ["OK", "bg-emerald-500/15 text-emerald-500"], attenzione: ["Attenzione", "bg-amber-500/15 text-amber-500"], bloccato: ["Bloccato", "bg-red-600 text-white"] };
const SemBadge = ({ s }) => { const [l, c] = SEM[s] || SEM.ok; return <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase ${c}`} data-testid="aff-semaforo">{l}</span>; };

function AgentAffRow({ r }) {
  return (
    <tr className={`border-b border-border/50 ${r.sconfino ? "bg-red-500/10" : ""}`} data-testid={`aff-row-${r.id}`}>
      <td className="py-2 pr-3">
        <Link to={`/partners/${r.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline" data-testid={`aff-link-${r.id}`}>{r.name}<ExternalLinkIcon /></Link>
        {r.insoluto?.attivo && <span className="ml-2 rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`aff-insoluto-${r.id}`}>Bloccato insoluto</span>}
      </td>
      <td className="py-2 pr-3 text-xs text-muted-foreground">{r.modalita_pagamento || "—"}</td>
      <td className="py-2 pr-3"><SemBadge s={r.semaforo} /></td>
      <td className="py-2 pr-3 text-right font-semibold">{eurN(r.fido_concesso)}</td>
      <td className="py-2 pr-3 text-right"><div className="font-semibold">{eurN(r.fido_impegnato)}</div><div className="text-[11px] text-muted-foreground">{r.commesse_aperte_n} comm.</div></td>
      <td className="py-2 text-right"><div className={`font-bold ${r.residuo != null && r.residuo < 0 ? "text-red-600 dark:text-red-400" : ""}`}>{r.residuo == null ? "—" : eurN(r.residuo)}</div></td>
    </tr>
  );
}

export default function Affidamenti() {
  const { user } = useAuth();
  const [aff, setAff] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [onlyEsposizione, setOnlyEsposizione] = useState(true);
  const [onlyPreventivi, setOnlyPreventivi] = useState(false);
  const [search, setSearch] = useState("");
  const isAdmin = user?.role === "admin";
  const agentView = !!aff?.agent_view;
  const canEdit = !!aff?.can_edit;
  const affClienti = (aff?.clienti || []).filter((r) =>
    (!onlyEsposizione || (((r.esposizione ?? r.fido_impegnato) || 0) > 0) || r.tipo === "prospect") &&
    (!onlyPreventivi || (r.preventivi_6m?.n || 0) > 0) &&
    (!search.trim() || (r.name || "").toLowerCase().includes(search.trim().toLowerCase()))
  );
  const loadAff = () => api.get("/crm-quotes/backoffice-affidamenti").then((r) => setAff(r.data)).catch(() => setAff(false));
  const syncCommesse = async () => {
    setSyncBusy(true);
    try {
      const r = await api.post("/admin/sync-commesse");
      toast.success(`Commesse aperte aggiornate: ${r.data.commesse_aperte ?? 0}`);
      await loadAff();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sincronizzazione commesse fallita");
    } finally { setSyncBusy(false); }
  };
  useEffect(() => { loadAff(); }, []);
  const [funnelOpen, setFunnelOpen] = useState(false);
  const canFunnel = ["admin", "commercial_manager", "head_of_partner", "back_office"].includes(user?.role);

  return (
    <div className="space-y-6" data-testid="affidamenti-page">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Affidamenti</h1>
          <p className="text-sm text-muted-foreground">Clienti con pagamento dilazionato: esposizione, fido concesso e ultimo bilancio.</p>
        </div>
        {canFunnel && (
          <FunnelTriggerButton label="Funnel affidamenti" onClick={() => setFunnelOpen(true)} data-testid="aff-open-funnel-btn" />
        )}
      </div>
      <AffidamentiFunnelDialog open={funnelOpen} onOpenChange={setFunnelOpen} />

      <PaymentMethodsManager />

      <Card className="p-5" data-testid="affidamenti-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight">
            <Wallet className="h-4 w-4 text-primary" /> Clienti con pagamento dilazionato
            {affClienti.length ? <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold">{affClienti.length}</span> : null}
          </h2>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={syncCommesse} disabled={syncBusy} data-testid="bo-sync-commesse-btn">
              {syncBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Aggiorna esposizione (commesse)
            </Button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-4">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca cliente per nome…" className="h-9 w-full sm:w-72" data-testid="aff-search" />
          <label className="flex cursor-pointer select-none items-center gap-2 text-xs font-medium" data-testid="aff-filtro-esposizione">
            <input type="checkbox" checked={onlyEsposizione} onChange={(e) => setOnlyEsposizione(e.target.checked)} className="h-4 w-4 accent-primary" />
            Solo clienti con esposizione attiva
          </label>
          {!agentView && (
          <label className="flex cursor-pointer select-none items-center gap-2 text-xs font-medium" data-testid="aff-filtro-preventivi">
            <input type="checkbox" checked={onlyPreventivi} onChange={(e) => setOnlyPreventivi(e.target.checked)} className="h-4 w-4 accent-primary" />
            Solo clienti con preventivi
          </label>
          )}
        </div>
        <p className="mb-3 mt-1 text-xs text-muted-foreground">{agentView
          ? <>Monitoraggio affidamenti dei tuoi clienti con pagamento dilazionato: modalità di pagamento, semaforo affidabilità, <b>fido concesso vs fido impegnato</b> e residuo disponibile. Clicca sul nome per aprire l'anagrafica.</>
          : <>Clienti con modalità di pagamento dilazionata (esclusi "Anticipato" e pagamenti a merce pronta). Ordini e preventivi degli ultimi 6 mesi, fido massimo concesso modificabile qui e ultimo bilancio. L'<b>esposizione</b> è calcolata dalle commesse aperte (id_t=42) sincronizzate dal gestionale. Clicca sul nome per aprire l'anagrafica.</>}</p>
        {aff === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Calcolo in corso…</div>
        ) : !affClienti.length ? (
          <p className="text-sm text-muted-foreground" data-testid="bo-affidamenti-empty">Nessun cliente con questi filtri.</p>
        ) : (
          <div className="overflow-x-auto">
            {agentView ? (
              <table className="w-full text-sm">
                <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="py-2 pr-3 font-medium">Cliente</th>
                    <th className="py-2 pr-3 font-medium">Modalità pagamento</th>
                    <th className="py-2 pr-3 font-medium">Stato</th>
                    <th className="py-2 pr-3 text-right font-medium">Fido concesso</th>
                    <th className="py-2 pr-3 text-right font-medium">Fido impegnato</th>
                    <th className="py-2 text-right font-medium">Residuo</th>
                  </tr>
                </thead>
                <tbody>
                  {affClienti.map((r) => <AgentAffRow key={r.id} r={r} />)}
                </tbody>
              </table>
            ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="py-2 pr-3 font-medium">Cliente</th>
                  <th className="py-2 pr-3 font-medium">Analisi</th>
                  <th className="py-2 pr-3 font-medium">Modalità pagamento</th>
                  <th className="py-2 pr-3 text-right font-medium">Ordini 6 mesi</th>
                  <th className="py-2 pr-3 text-right font-medium">Preventivi 6 mesi</th>
                  <th className="py-2 pr-3 text-right font-medium">Esposizione</th>
                  <th className="py-2 pr-3 text-right font-medium">Residuo</th>
                  <th className="py-2 pr-3 font-medium">Fido massimo (€)</th>
                  <th className="py-2 text-right font-medium">Ultimo bilancio</th>
                </tr>
              </thead>
              <tbody>
                {affClienti.map((r) => <AffRow key={r.id} r={r} onSaved={loadAff} canEdit={canEdit} />)}
              </tbody>
            </table>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
